import { useQuery } from '@tanstack/react-query';
import { Card } from '@safetyhub/ui';
import { adminApi } from '../lib/api';
import { SystemInfoCard } from '../components/SystemInfoCard';

export default function Dashboard() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: () => adminApi.getDashboard(),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">로딩 중...</div>
      </div>
    );
  }

  if (error || !data?.success) {
    return (
      <div className="bg-red-50 p-4 rounded text-red-600">
        데이터를 불러오는데 실패했습니다.
      </div>
    );
  }

  const stats = data.data!;

  return (
    <div>
      <h2 className="text-2xl font-bold text-gray-900 mb-6">대시보드</h2>

      {/* 전체 통계 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="전체 조직"
          value={stats.totalOrganizations}
          icon="🏢"
          color="blue"
        />
        <StatCard
          title="전체 사용자"
          value={stats.totalUsers}
          icon="👥"
          color="green"
        />
        <StatCard
          title="전체 현장"
          value={stats.totalSites}
          icon="📍"
          color="purple"
        />
        <StatCard
          title="전체 점검"
          value={stats.totalInspections}
          icon="📋"
          color="orange"
        />
      </div>

      {/* 최근 활동 */}
      <Card padding="xl" radius="lg" className="mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          이번 주 활동
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <ActivityItem
            label="신규 조직"
            value={stats.recentActivity.organizationsThisWeek}
          />
          <ActivityItem
            label="신규 사용자"
            value={stats.recentActivity.usersThisWeek}
          />
          <ActivityItem
            label="신규 점검"
            value={stats.recentActivity.inspectionsThisWeek}
          />
        </div>
      </Card>

      {/* 시스템 정보 */}
      <SystemInfoCard />
    </div>
  );
}

function StatCard({
  title,
  value,
  icon,
  color,
}: {
  title: string;
  value: number;
  icon: string;
  color: 'blue' | 'green' | 'purple' | 'orange';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <Card padding="xl" radius="lg">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-3xl font-bold text-gray-900 mt-1">
            {value.toLocaleString()}
          </p>
        </div>
        <div
          className={`w-12 h-12 rounded-full flex items-center justify-center text-2xl ${colorClasses[color]}`}
        >
          {icon}
        </div>
      </div>
    </Card>
  );
}

function ActivityItem({ label, value }: { label: string; value: number }) {
  return (
    <div className="text-center p-4 bg-gray-50 rounded">
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="text-sm text-gray-500">{label}</p>
    </div>
  );
}
